import React, {Dispatch, ReactNode, SetStateAction, useState} from "react";
import {FastailyApi} from "../../../services/api/FastailyApi";
import {GameComponentClassic} from "./GameComponentClassic";
import {TFunction} from "i18next";
import {UiButton} from "../../../components/ui-components/button/uiButton";
import CountdownTimer from "../../../components/utils/CountdownTimer";

export class GameComponentFastaily extends GameComponentClassic{

    private readonly SESSION_KEY = "fastaily-hide-info-popup";

    override fetchWords(difficulty: Number | null, setText: React.Dispatch<React.SetStateAction<string[]>>): void {
        FastailyApi.getTextFastaily()
            .then((words: string[]) => {
                setText(words);
            })
            .catch((error: any) => {
                console.error("Erreur lors de la récupération des mots :", error);
            });
    }


    override getBottomInfo(currentScore: number, time: number, resetGame: () => void, t: TFunction<"translation", undefined>): ReactNode {
        return (
            <div className="flex justify-between w-full">
                <p>{t("game.x-words", {count: currentScore})}</p>
                <div className="flex flex-col items-end gap-2">
                    <div className="flex justify-center">
                        <p>{t("game.x-time-left", {count: time})}</p>
                    </div>
                </div>
            </div>
        );
    }

    override getTopInfo(t: TFunction<"translation", undefined>, showInformationsPopup: () => void): React.ReactNode {
        return (
            <>
                <div className="flex justify-between w-full">
                    <CountdownTimer/>
                    <UiButton
                        className="flex justify-end mb-2"
                        text={"Informations"}
                        onClick={showInformationsPopup}
                        icon="bubble-chat-question"
                    />
                </div>
            </>
        );
    }

    private getNextEightAM(): Date {
        const now = new Date();
        const nextEightAM = new Date();

        if (now.getHours() >= 8) {
            // Si on est après 8h, prendre 8h demain
            nextEightAM.setDate(now.getDate() + 1);
        }
        // Sinon, prendre 8h aujourd'hui
        nextEightAM.setHours(8, 0, 0, 0);
        return nextEightAM;
    }

    private setCookieUntilEightAM(name: string, value: string): void {
        const expires = this.getNextEightAM();
        document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/`;
    }

    private async checkUserCanPlay(t: TFunction<"translation", undefined>, userId: number, gameModeId: number): Promise<{
        canPlay: boolean,
        reason?: string,
        score?: number,
        date?: string,
        rank?: number
    }> {
        try {
            const participation = await FastailyApi.hasUserParticipatedToday(userId, gameModeId);
            if (participation && participation.id !== undefined) {
                // Supposons que participation contient score, date, rank
                this.setCookieUntilEightAM('fastaily-played-today', 'true');
                return {
                    canPlay: false,
                    reason: t("game.already-participated-fastaily-today"),
                    score: participation.score,
                    date: participation.date,
                    rank: participation.rank
                };
            }
        } catch (error) {
            console.error("Erreur lors de la vérification:", error);
        }
        return {canPlay: true};
    };

    override async canUserPlay(t: TFunction<"translation", undefined>, userId: number, gameModeId: number): Promise<{canPlay: boolean, reason?: string}> {
        return await this.checkUserCanPlay(t, userId, gameModeId);
    }

    override shouldShowInformationPopupOnStart(): boolean {
        const hideInfoPopup = localStorage.getItem(this.SESSION_KEY) === "true";
        console.log(hideInfoPopup)

        return !hideInfoPopup;
    }

    public setCookieAfterGame(): void {
        this.setCookieUntilEightAM('fastaily-played-today', 'true');
    }

    override getInformationPopup(t: TFunction<"translation", undefined>, closePopup: Dispatch<SetStateAction<boolean>>): React.ReactNode {
        const FastailyInformationPopup: React.FC = () => {
            const initialHideValue = localStorage.getItem(this.SESSION_KEY) === "true";
            const [hidePopup, setHidePopup] = useState(initialHideValue);

            const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                const checked = event.target.checked;
                setHidePopup(checked);

                if (checked) {
                    localStorage.setItem(this.SESSION_KEY, "true");
                } else {
                    localStorage.removeItem(this.SESSION_KEY);
                }
            };

            return (
                <div className="flex flex-col gap-4 max-w-2xl h-128">
                    <h2 className={"flex justify-center"}>
                        {t("fastaily.popup-title")}
                    </h2>

                    <pre className="whitespace-pre-wrap break-words font-sans p-6">
                        {t("fastaily.popup-content")}
                    </pre>

                    {(
                        <p className={"flex justify-center gap-2"}>
                            <input
                                type={"checkbox"}
                                checked={hidePopup}
                                onChange={handleCheckboxChange}
                            />
                            {t("fastaily.popup-checkbox")}
                        </p>
                    )}

                    <UiButton
                        icon="close" className="absolute top-1 right-1 w-8 bg-transparent"
                        onClick={() => closePopup(false)}
                    />
                </div>
            );
        };

        return <FastailyInformationPopup />;
    }

    override getAccessPopup(t: TFunction<"translation", undefined>, closePopup: Dispatch<SetStateAction<boolean>>): React.ReactNode {
        const FastailyAccessPopup: React.FC = () => {
            return (
                <div className="flex flex-col gap-4 max-w-2xl h-128">
                    <h2 className={"flex justify-center"}>
                        {t("fastaily.popup-already-played-title")}
                    </h2>
                    <pre className="whitespace-pre-wrap break-words font-sans p-6">
                    {t("fastaily.popup-already-played-content")}
                </pre>
                </div>
            );
        }

        return <FastailyAccessPopup />;
    }

}